/**
 * RetailAR — one article, stood on the real floor through the phone camera.
 *
 * The solid is the same one the room planner draws : same archetypes, same
 * dimensions read off the ioi item, same CC0 materials. Only the framing
 * changes — metres instead of centimetres, origin under the object rather than
 * in the corner of a room.
 *
 * What it does on a device :
 *
 *   * WebXR immersive-ar with hit-test — the phone finds the floor, a reticle
 *     follows it, one tap puts the piece down at its real size ;
 *   * tapping a drawer front pulls it out, tapping a door swings it open ;
 *   * two fingers turn it, a drag slides it along the floor.
 *
 * Where WebXR is missing — every iPhone today, Safari has no AR session — the
 * page falls back to the same solid on a turntable. That is a fallback, not
 * augmented reality, and the page says so rather than pretending.
 */

/* global frappe */

const AR_NEAR = 0.01;
const AR_FAR = 40;

class RetailAR {
	constructor(container, options = {}) {
		this.root = typeof container === "string" ? document.querySelector(container) : container;
		this.options = Object.assign({ item: null, assetRoot: null }, options);
		this.model = null;
		this.meshes = [];
		this.openState = new Map();
		this.placed = false;
		this._tweens = [];
	}

	// ------------------------------------------------------------------ boot
	async mount() {
		this.root.innerHTML = TEMPLATE;
		this.el = {
			stage: this.root.querySelector("#ar-stage"),
			title: this.root.querySelector("#ar-title"),
			dims: this.root.querySelector("#ar-dims"),
			actions: this.root.querySelector("#ar-actions"),
			hint: this.root.querySelector("#ar-hint"),
			start: this.root.querySelector("#ar-start"),
			overlay: this.root.querySelector("#ar-overlay"),
		};

		const shared = window.RetailGLShared;
		if (!shared) throw new Error("RetailGLShared absent : room3d_gl.js n'est pas chargé");
		this.assetRoot = this.options.assetRoot === null ? shared.assetRoot : this.options.assetRoot;
		this.tiles = shared.tiles;
		const THREE = await shared.loadThree(this.assetRoot);
		this.THREE = THREE;

		this.model = await frappe.xcall("ioi_retail_suite.api.ar_model", { item: this.options.item });
		this._buildRenderer();
		this._buildModel();
		this._describe();
		await this._offerSession();
		this._loop();
		return this;
	}

	_buildRenderer() {
		const THREE = this.THREE;
		this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 0.95;
		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		this.renderer.xr.enabled = true;
		this.el.stage.appendChild(this.renderer.domElement);

		this.scene = new THREE.Scene();
		this.camera = new THREE.PerspectiveCamera(58, 1, AR_NEAR, AR_FAR);

		this.scene.add(new THREE.HemisphereLight(0xffffff, 0xa8afb8, 1.1));
		this.sun = new THREE.DirectionalLight(0xfff2e0, 2.2);
		this.sun.position.set(1.4, 3.2, 1.8);
		this.sun.castShadow = true;
		this.sun.shadow.mapSize.set(1024, 1024);
		this.sun.shadow.camera.left = -2;
		this.sun.shadow.camera.right = 2;
		this.sun.shadow.camera.top = 2;
		this.sun.shadow.camera.bottom = -2;
		this.sun.shadow.camera.far = 12;
		this.sun.shadow.bias = -0.0005;
		this.scene.add(this.sun);

		// a gradient sky, so the standard materials have something to reflect
		const size = 128;
		const canvas = document.createElement("canvas");
		canvas.width = canvas.height = size;
		const context = canvas.getContext("2d");
		const gradient = context.createLinearGradient(0, 0, 0, size);
		gradient.addColorStop(0, "#ffffff");
		gradient.addColorStop(1, "#8d949c");
		context.fillStyle = gradient;
		context.fillRect(0, 0, size, size);
		const sky = new THREE.CanvasTexture(canvas);
		sky.mapping = THREE.EquirectangularReflectionMapping;
		sky.colorSpace = THREE.SRGBColorSpace;
		const pmrem = new THREE.PMREMGenerator(this.renderer);
		this.scene.environment = pmrem.fromEquirectangular(sky).texture;
		pmrem.dispose();
		sky.dispose();

		this._resize();
		window.addEventListener("resize", () => this._resize());
	}

	_resize() {
		const width = this.el.stage.clientWidth || 360;
		const height = this.el.stage.clientHeight || 480;
		this.renderer.setSize(width, height, false);
		this.camera.aspect = width / Math.max(1, height);
		this.camera.updateProjectionMatrix();
	}

	// ----------------------------------------------------------------- model
	_material(key) {
		const THREE = this.THREE;
		this._materials = this._materials || new Map();
		if (this._materials.has(key)) return this._materials.get(key);
		const route = this.assetRoot + "textures/";
		const loader = this._loader || (this._loader = new THREE.TextureLoader());
		const wrap = (texture, srgb) => {
			texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
			texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
			if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
			return texture;
		};
		const material = new THREE.MeshStandardMaterial({
			vertexColors: true,
			roughness: 0.85,
			metalness: key === "metal" ? 0.8 : 0.02,
		});
		material.map = wrap(loader.load(route + key + ".jpg"), true);
		material.normalMap = wrap(loader.load(route + key + "_n.jpg"), false);
		material.normalScale = new THREE.Vector2(0.55, 0.55);
		material.roughnessMap = wrap(loader.load(route + key + "_r.jpg"), false);
		this._materials.set(key, material);
		return material;
	}

	/** One tile covers the material's own repeat length, converted to metres. */
	_scaleUV(geometry, w, d, h, key) {
		const catalogue = (this.model && this.model.materials) || {};
		const entry = catalogue[key] || {};
		const tile = (entry.tile_cm || this.tiles[key] || this.model.default_tile_cm || 50) / 100;
		const uv = geometry.attributes.uv;
		const spans = [[h, d], [h, d], [w, h], [w, h], [w, d], [w, d]];
		for (let face = 0; face < 6; face++) {
			for (let corner = 0; corner < 4; corner++) {
				const index = face * 4 + corner;
				uv.setXY(index, uv.getX(index) * (spans[face][0] / tile),
					uv.getY(index) * (spans[face][1] / tile));
			}
		}
		uv.needsUpdate = true;
		return geometry;
	}

	_buildModel() {
		const THREE = this.THREE;
		this.piece = new THREE.Group();
		// z is up everywhere else in this app ; tip the whole piece once
		this.piece.rotation.x = -Math.PI / 2;
		this.piece.visible = false;
		this.scene.add(this.piece);

		for (const part of this.model.parts) {
			const key = part.material || "laminate";
			const geometry = new THREE.BoxGeometry(part.w, part.d, part.h);
			this._scaleUV(geometry, part.w, part.d, part.h, key);
			const tint = new THREE.Color(1, 1, 1).lerp(
				new THREE.Color(part.color || "#b9bfc7"),
				Math.max(0, Math.min(1, 0.08 + part.tone * 0.15))
			);
			const count = geometry.attributes.position.count;
			const colours = new Float32Array(count * 3);
			for (let i = 0; i < count; i++) {
				colours[i * 3] = tint.r;
				colours[i * 3 + 1] = tint.g;
				colours[i * 3 + 2] = tint.b;
			}
			geometry.setAttribute("color", new THREE.BufferAttribute(colours, 3));

			const mesh = new THREE.Mesh(geometry, this._material(key));
			mesh.castShadow = true;
			mesh.receiveShadow = true;
			mesh.position.set(part.x + part.w / 2, part.y + part.d / 2, part.z + part.h / 2);
			mesh.userData.part = part.index;
			mesh.userData.home = mesh.position.clone();
			this.piece.add(mesh);
			this.meshes[part.index] = mesh;
		}

		// a soft disc under the piece, so it does not float on a real floor
		const shadow = new THREE.Mesh(
			new THREE.CircleGeometry(
				Math.max(this.model.dimensions_cm.w, this.model.dimensions_cm.d) / 160, 32),
			new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.22 })
		);
		shadow.position.z = 0.002;
		this.piece.add(shadow);

		this.reticle = new THREE.Mesh(
			new THREE.RingGeometry(0.08, 0.1, 32).rotateX(-Math.PI / 2),
			new THREE.MeshBasicMaterial({ color: 0x0058a3 })
		);
		this.reticle.matrixAutoUpdate = false;
		this.reticle.visible = false;
		this.scene.add(this.reticle);
	}

	_describe() {
		const dims = this.model.dimensions_cm;
		this.el.title.textContent = this.model.label;
		this.el.dims.textContent =
			`${Math.round(dims.w)} × ${Math.round(dims.d)} × ${Math.round(dims.h)} cm · ${this.model.item}`;
		this.el.actions.innerHTML = (this.model.openings || [])
			.map((opening) => `<button class="ar-btn" data-open="${opening.part}">${
				frappe.utils.escape_html(opening.label)}</button>`)
			.join("");
		this.el.actions.querySelectorAll("[data-open]").forEach((node) => {
			node.onclick = () => this.toggle(Number(node.dataset.open));
		});
	}

	// -------------------------------------------------------- opening a part
	/** Pull a drawer out, or swing a door — and put it back on a second tap. */
	toggle(partIndex) {
		const opening = (this.model.openings || []).find((o) => o.part === partIndex);
		const mesh = this.meshes[partIndex];
		if (!opening || !mesh) return false;
		const open = !this.openState.get(partIndex);
		this.openState.set(partIndex, open);

		if (opening.kind === "drawer") {
			const travel = (this.model.dimensions_cm.d / 100) * (opening.travel || 0.75);
			this._tween(mesh, "position", mesh.userData.home.clone().add(
				new this.THREE.Vector3(0, open ? travel : 0, 0)));
		} else {
			// hinge on the outer vertical edge of the panel, not on its centre
			const part = this.model.parts[partIndex];
			const hingeX = opening.hinge === "left" ? part.x : part.x + part.w;
			const angle = open ? (opening.swing || 100) * (Math.PI / 180) : 0;
			this._tweenHinge(mesh, hingeX, angle);
		}
		const button = this.el.actions.querySelector(`[data-open="${partIndex}"]`);
		if (button) button.classList.toggle("is-open", open);
		return open;
	}

	_tween(mesh, property, target) {
		const from = mesh[property].clone();
		this._tweens.push({
			mesh, property, from, target, start: performance.now(), duration: 420,
			apply: (t) => mesh[property].lerpVectors(from, target, t),
		});
	}

	_tweenHinge(mesh, hingeX, angle) {
		const part = this.model.parts[mesh.userData.part];
		const from = mesh.rotation.z;
		const home = mesh.userData.home;
		this._tweens.push({
			start: performance.now(), duration: 420,
			apply: (t) => {
				const a = from + (angle - from) * t;
				mesh.rotation.z = a;
				// rotate the panel's centre around the hinge so the edge stays put
				const dx = home.x - hingeX;
				const dy = home.y - (part.y + part.d / 2);
				mesh.position.x = hingeX + dx * Math.cos(a) - dy * Math.sin(a);
				mesh.position.y = (part.y + part.d / 2) + dx * Math.sin(a) + dy * Math.cos(a);
			},
		});
	}

	_stepTweens(now) {
		if (!this._tweens.length) return;
		this._tweens = this._tweens.filter((tween) => {
			const raw = Math.min(1, (now - tween.start) / tween.duration);
			const eased = 1 - Math.pow(1 - raw, 3);
			tween.apply(eased);
			return raw < 1;
		});
	}

	// ------------------------------------------------------------ XR session
	async _offerSession() {
		const supported = !!(navigator.xr && await navigator.xr.isSessionSupported("immersive-ar")
			.catch(() => false));
		this.supported = supported;
		if (supported) {
			this.el.start.textContent = "Voir chez moi";
			this.el.start.onclick = () => this._startSession();
			this.el.hint.textContent =
				"Visez le sol, puis touchez l'écran pour poser le meuble à sa taille réelle.";
		} else {
			this.el.start.hidden = true;
			this.el.hint.innerHTML =
				"<b>La réalité augmentée n'est pas disponible sur cet appareil.</b><br>" +
				"Elle demande Chrome sur Android ; Safari n'expose pas encore de session AR. " +
				"Le meuble reste manipulable ci-dessous, à ses dimensions réelles.";
			this._turntable();
		}
	}

	/** Desktop and iPhone : the same solid, on a turntable. */
	_turntable() {
		const dims = this.model.dimensions_cm;
		const span = Math.max(dims.w, dims.d, dims.h) / 100;
		this.piece.visible = true;
		this.piece.position.set(0, 0, 0);
		this.camera.position.set(span * 1.1, span * 0.85, span * 1.5);
		this.camera.lookAt(0, (dims.h / 100) * 0.45, 0);
		this.spin = { yaw: 0, dragging: false, last: 0 };

		const canvas = this.renderer.domElement;
		canvas.addEventListener("pointerdown", (event) => {
			this.spin.dragging = true;
			this.spin.last = event.clientX;
			canvas.setPointerCapture(event.pointerId);
			this._tapAt(event);
		});
		canvas.addEventListener("pointermove", (event) => {
			if (!this.spin.dragging) return;
			this.spin.yaw += (event.clientX - this.spin.last) * 0.01;
			this.spin.last = event.clientX;
			this.piece.rotation.z = this.spin.yaw;
		});
		canvas.addEventListener("pointerup", () => { this.spin.dragging = false; });
	}

	async _startSession() {
		const session = await navigator.xr.requestSession("immersive-ar", {
			requiredFeatures: ["hit-test"],
			optionalFeatures: ["dom-overlay", "light-estimation"],
			domOverlay: { root: this.el.overlay },
		});
		this.session = session;
		this.root.classList.add("is-xr");
		await this.renderer.xr.setSession(session);

		const viewerSpace = await session.requestReferenceSpace("viewer");
		this.hitSource = await session.requestHitTestSource({ space: viewerSpace });
		this.localSpace = await session.requestReferenceSpace("local");

		session.addEventListener("select", () => this._onSelect());
		session.addEventListener("end", () => {
			this.root.classList.remove("is-xr");
			this.session = null;
			this.hitSource = null;
			this.placed = false;
			this.piece.visible = false;
		});
	}

	/**
	 * A tap either puts the piece down, or opens whatever it landed on.
	 *
	 * In an XR session there are no pointer events on the canvas : the only
	 * thing the session reports is `select`, aimed straight down the middle of
	 * the view. So the ray is the viewer's, not a finger's.
	 */
	_onSelect() {
		if (!this.placed) {
			if (!this.reticle.visible) return;
			this.piece.position.setFromMatrixPosition(this.reticle.matrix);
			this.piece.visible = true;
			this.placed = true;
			this.reticle.visible = false;
			this.el.hint.textContent = "Visez un tiroir et touchez l'écran pour l'ouvrir.";
			return;
		}
		const THREE = this.THREE;
		const ray = this._ray || (this._ray = new THREE.Raycaster());
		const camera = this.renderer.xr.getCamera();
		ray.set(
			camera.getWorldPosition(new THREE.Vector3()),
			camera.getWorldDirection(new THREE.Vector3())
		);
		// nothing openable under the ray simply does nothing
		const hit = ray.intersectObjects(this.meshes.filter(Boolean), false)[0];
		if (hit) this.toggle(hit.object.userData.part);
	}

	/** Outside a session a real tap has coordinates, so use them. */
	_tapAt(event) {
		const THREE = this.THREE;
		const rect = this.renderer.domElement.getBoundingClientRect();
		const pointer = new THREE.Vector2(
			((event.clientX - rect.left) / rect.width) * 2 - 1,
			-((event.clientY - rect.top) / rect.height) * 2 + 1
		);
		const ray = this._ray || (this._ray = new THREE.Raycaster());
		ray.setFromCamera(pointer, this.camera);
		const hit = ray.intersectObjects(this.meshes.filter(Boolean), false)[0];
		if (hit) this.toggle(hit.object.userData.part);
	}

	// ------------------------------------------------------------------ loop
	_loop() {
		this.renderer.setAnimationLoop((time, frame) => {
			this._stepTweens(time || performance.now());
			if (frame && this.hitSource && !this.placed) {
				const hits = frame.getHitTestResults(this.hitSource);
				if (hits.length) {
					const pose = hits[0].getPose(this.localSpace);
					this.reticle.visible = true;
					this.reticle.matrix.fromArray(pose.transform.matrix);
				} else {
					this.reticle.visible = false;
				}
			}
			this.renderer.render(this.scene, this.camera);
		});
	}
}

const TEMPLATE = `
<div class="ar">
	<div id="ar-stage" class="ar-stage"></div>
	<div id="ar-overlay" class="ar-overlay">
		<div class="ar-card">
			<b id="ar-title"></b>
			<span id="ar-dims"></span>
			<p id="ar-hint" class="ar-hint"></p>
			<div id="ar-actions" class="ar-actions"></div>
			<button id="ar-start" class="ar-btn ar-primary">Voir chez moi</button>
		</div>
	</div>
</div>`;

if (typeof window !== "undefined") window.RetailAR = RetailAR;
if (typeof module !== "undefined") module.exports = RetailAR;
